import { CumulativeBoosterID } from "./cumulative_booster_id.js";
import { CumulativeBoosterState } from "./cumulative_booster_state.js";

/**
 * Base lifecycle for a booster used during a level.
 *
 * The context is deliberately supplied by the game layer. This keeps boosters
 * testable and prevents the base mechanic from depending directly on GameScreen.
 */
export abstract class CumulativeBooster<TContext>
{
  public readonly id: CumulativeBoosterID;

  private current_state = CumulativeBoosterState.Ready;
  private context: TContext | null = null;

  public onStateChanged: (
    booster: CumulativeBooster<TContext>,
    state: CumulativeBoosterState
  ) => void = () => {};

  protected constructor( id: CumulativeBoosterID )
  {
    this.id = id;
  }

  public get state(): CumulativeBoosterState
  {
    return this.current_state;
  }

  public get isActive(): boolean
  {
    return this.current_state === CumulativeBoosterState.Active;
  }

  protected get activeContext(): TContext | null
  {
    return this.context;
  }

  public activate( context: TContext ): boolean
  {
    if ( this.current_state !== CumulativeBoosterState.Ready )
      return false;

    this.context = context;
    this.setState( CumulativeBoosterState.Active );
    this.onActivate( context );
    return true;
  }

  public update( dt: number ): void
  {
    if ( !this.isActive || this.context === null )
      return;

    if ( !Number.isFinite( dt ) || dt < 0 )
      throw new Error( "Booster update delta must be a finite non-negative number" );

    this.onUpdate( this.context, dt );
  }

  public cancel(): void
  {
    if ( !this.isActive || this.context === null )
      return;

    const context = this.context;
    this.context = null;
    this.onDeactivate( context, true );
    this.setState( CumulativeBoosterState.Cancelled );
  }

  /** Prepares the effect instance for its next owned use. */
  public reset(): void
  {
    if ( this.isActive )
      this.cancel();

    this.context = null;
    this.onReset();
    this.setState( CumulativeBoosterState.Ready );
  }

  protected complete(): void
  {
    if ( !this.isActive || this.context === null )
      return;

    const context = this.context;
    this.context = null;
    this.onDeactivate( context, false );
    this.setState( CumulativeBoosterState.Completed );
  }

  protected abstract onActivate( context: TContext ): void;

  protected onUpdate( _context: TContext, _dt: number ): void
  {
  }

  protected onDeactivate( _context: TContext, _cancelled: boolean ): void
  {
  }

  protected onReset(): void
  {
  }

  private setState( state: CumulativeBoosterState ): void
  {
    if ( this.current_state === state )
      return;

    this.current_state = state;
    this.onStateChanged( this, state );
  }
}
